import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api';
import styles from '../styles/Auth.module.css';

const PasswordResetPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // Password strength requirements
  const requirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    special: /[!@#$%^&*]/.test(newPassword),
  };

  const getPasswordStrength = () => {
    const metRequirements = Object.values(requirements).filter(Boolean).length;
    if (metRequirements <= 2) return 'weak';
    if (metRequirements <= 4) return 'medium';
    return 'strong';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (Object.values(requirements).some(req => !req)) {
      setError('Please meet all password requirements');
      return;
    }

    try {
      await api.resetPassword(newPassword);
      setSuccess('Password updated successfully! Redirecting to login...');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError('Failed to reset password. Please try again.');
    }
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <h2 className={styles.authTitle}>Reset Password</h2>
        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.formGroup}>
            <label htmlFor="newPassword" className={styles.label}>
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={styles.input}
              required
            />
            <div className={styles.strengthMeter}>
              <div
                className={`${styles.strengthIndicator} ${styles[getPasswordStrength()]}`}
              />
            </div>
            <div className={styles.requirements}>
              <div className={styles.requirementItem}>
                <span className={requirements.length ? styles.checkIcon : styles.xIcon}>
                  {requirements.length ? '✓' : '✗'}
                </span>
                {' '}At least 8 characters
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.uppercase ? styles.checkIcon : styles.xIcon}>
                  {requirements.uppercase ? '✓' : '✗'}
                </span>
                {' '}One uppercase letter
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.lowercase ? styles.checkIcon : styles.xIcon}>
                  {requirements.lowercase ? '✓' : '✗'}
                </span>
                {' '}One lowercase letter
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.number ? styles.checkIcon : styles.xIcon}>
                  {requirements.number ? '✓' : '✗'}
                </span>
                {' '}One number
              </div>
              <div className={styles.requirementItem}>
                <span className={requirements.special ? styles.checkIcon : styles.xIcon}>
                  {requirements.special ? '✓' : '✗'}
                </span>
                {' '}One special character (!@#$%^&*)
              </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={styles.input}
              required
            />
          </div>

          <button
            type="submit"
            className={styles.button}
            disabled={!Object.values(requirements).every(Boolean) || !confirmPassword}
          >
            Reset Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetPage;
